import { AccountBookingPayload, BookingLocationContact, PickupServiceCheckState, ServiceCheckStatus } from "../BookingEntities";
import { PaymentOption } from "../../Payment/PaymentEntities";
import { AddressV2 } from "../../../Services/MakeBookingContracts";
import { BookingTimeV2 } from "../../GbBooking/BookingV2Entities";
import { DeliveryOptionKind } from "../Parcel/ParcelEntities";
import { AddressPointDetails } from "../../../Services/AddressPointsContracts";

/** Data for Silver Service Exec bookings */
export interface SilverExecData {
    Passengers: number;
    Luggage: number;
    FlightNumber: string;
}

/** Initial/default values for Silver Exec data */
export const DefaultSilverExecData: SilverExecData = {
    Passengers: 1,
    Luggage: 0,
    FlightNumber: ""
};

/** Initial/default values for Silver Exec XL data */
export const DefaultSilverExecXlData: SilverExecData = {
    Passengers: 5,
    Luggage: 0,
    FlightNumber: ""
};

/** Shared application state for the booking under construction in the create booking UI */
export interface BookingWorkFlowState {
    PickupServiceCheck: PickupServiceCheckState;
    
    /** Google Maps Place ID for the pickup location. Stored to support state updates that need the placeId. */
    PickupPlaceId: string;
    
    PaymentOption?: PaymentOption;

    /** When true, adds a debug UI component to show the internal loading status */
    ShowDiagnosticUI: boolean;

    /** Whether the User has selected to make the booking on Account. When true, additional booking input fields are available */
    IsOnAccount: boolean;

    /** Account specific information only for bookings with accounts. */
    AccountData: AccountBookingPayload | null;

    /** It is user's device information(used for fraud detection), it is set using braintree library,it is a required field to successfully do an app payment
     * Booking api decides whether it is valid card booking by checking existence of device data field.
     * MPS also sends this devicedata to braintree along with transaction request,braintree uses kount for fraud detection.
    */
    DeviceData?: string;

    /** Template name text for favourites */
    TemplateName?: string;
    
    /** Booking timing details to use for GB booking service. */
    BookingTimeV2: BookingTimeV2;

    /** user selected delivery option when parcel widget booking  */
    DeliveryOption: DeliveryOptionKind | null;

    /** Delivery option error */
    DeliveryOptionError: string | null;

    /**
     * Trip ID associated with any Google Maps API calls for the booking being created.
     * This supports the per-trip billing mechanism in Google ODRD.
     * Format: a GUID.
     * It's typed as nullable because it is briefly null at startup until it is first loaded from the server. Gotta be careful.
     */
    GoogleOdrdTripId: string | null;

    /** The list of locations for the booking. It can have up to 5 locations. */
    Locations: BookingLocationInfo[];

    /** Number of seats requested by the user. Only applicable for Maxi Taxi and SilverServiceExec XL in V2 booking flow. */
    NumberOfSeats: number | null;

    /** Silver Service Exec booking data (passengers, luggage, flight number) */
    SilverExec: SilverExecData | null;

    /** Silver Service Exec XL booking data (passengers, luggage, flight number) */
    SilverExecXl: SilverExecData | null;

    /** Silver Service Exec validation error message */
    SilverExecError: string | null;
}

/** All the data related to a location (pickup / stopover / dropoff) of a booking that is being constructed in the booking widget. */
export interface BookingLocationInfo {

    /** Contact person name and phone number for this location. */
    Contact: BookingLocationContact;

    /** per-location driver notes */
    DriverNotes: string | null;

    /** Address geopoint, place id, and other metadata for this location */
    Address: AddressV2 | null;

    /** The Favourite Address record matching [Address], if the user selected the address by picking a favourite. */
    Favourite: AddressPointDetails | null;

    /** If current location is selected from a list of curated locations, record the LocationAddressId to be used in the fare estimate request and booking request. */
    CuratedLocationAddressId: number | null;

    /** The ZoneId of the curated location if this location is from a curated location zone. Used for zone-based service restrictions (e.g., airports). */
    CuratedLocationZoneId: number | null;
}

/** Initial value of additional address details store slices. */
const contactInitialValue: BookingLocationContact = {
    Phone: null
};

export const locationInitialValue: BookingLocationInfo = {
    Contact: contactInitialValue,
    DriverNotes: null,
    Address: null,
    Favourite: null,
    CuratedLocationAddressId: null,
    CuratedLocationZoneId: null
};

/** Initial value of the Booking state */
export const DefaultBookingWorkFlowState: BookingWorkFlowState = {
    PickupServiceCheck: {
        status: ServiceCheckStatus.NoInputSelected
    },
    PickupPlaceId: "",
    ShowDiagnosticUI: false,
    IsOnAccount: false,
    AccountData: null,
    BookingTimeV2: {
        IsImmediate: true
    },

    DeliveryOption: null,
    DeliveryOptionError: null,
    GoogleOdrdTripId: null, // populated during startup
    Locations: [locationInitialValue, locationInitialValue],// default pickup and dropoff locations in order to display initial input fields.
    NumberOfSeats: null,
    SilverExec: null,
    SilverExecXl: null,
    SilverExecError: null,
};

/** Object to pass to the dispatcher when an address field is dragged and dropped to a different location. */
export interface ReOrderedLocation {
    Location: BookingLocationInfo;
    NewIndex: number;
}
